import {
  publishMedia, publishMenu, publishPlaylist, createPlaylist,
  publishAudio, publishAudioPlaylist, createAudioPlaylist,
  publishStopScreen, publishTransition,
} from '@meno/ui/lib/api'
import { withCompanions } from '@/lib/companions'
import type { PublishPayload } from '@/components/PublishDialog'
import type { PickedItem } from '@/components/PlaylistContentPicker'

// Stream picked audio to screens/groups. One track streams directly; picking
// several at once bundles them into a brand-new audio playlist and streams
// that — the audio analogue of the content auto-playlist above. Returns the
// display name of what was streamed (or null when no audio was picked).
export async function publishAudioPicks(
  picked: PickedItem[],
  targets: { screen_ids: number[]; group_ids: number[]; start_at?: string | null; end_at?: string | null },
): Promise<{ name: string } | null> {
  const audios = picked.filter((p): p is Extract<PickedItem, { kind: 'audio' }> => p.kind === 'audio')
  if (audios.length === 0) return null
  // Same guard as publishQuickTarget — an audio publish pushes a
  // content_update too, so leaving this path open would let an operator
  // sidestep the limit by alternating content and audio.
  guardPublishRate()
  if (audios.length === 1) {
    await publishAudio(audios[0].id, targets)
    return { name: audios[0].original_name }
  }
  const name = `${audios[0].original_name} +${audios.length - 1}`
  const { data } = await createAudioPlaylist({
    name,
    tracks: audios.map(a => ({ audio_id: a.id })),
  })
  await publishAudioPlaylist(data.id, targets)
  return { name }
}

// ── Quick Publish service ───────────────────────────────────────────────────
// The ONE routing layer for every "pick something → push to screens" flow
// (Dashboard Quick Publish, Content Hub "Publish content", …). Each kind maps
// to the same per-entity publish endpoint the entity's own page uses, so a
// quick publish and a "normal" publish are literally the same call — including
// playlist companion co-publishing and the frontend-only transition stamp.

// What's about to be pushed. Backend entities carry their id; a transition is
// identified by its catalog key (frontend-only, stamped into localStorage). A
// `new_playlist` target defers creating a playlist until publish time — used
// when several content pieces are picked at once so they play as a sequence.
export type QuickPublishTarget =
  | { kind: 'media' | 'menu' | 'playlist' | 'audio' | 'audio_playlist' | 'stop_screen'; id: number }
  | { kind: 'transition'; key: string }
  | {
      kind: 'new_playlist'
      name: string
      mediaIds: number[]
      linkIds: number[]
      // Per-item timing + order so the auto-created playlist behaves exactly
      // like a hand-built one: videos keep their length, images get a default
      // dwell, and total_duration is computed server-side (shown on the card).
      mediaDurations: Record<number, number>
      linkDurations: Record<number, number>
      mediaOrder: Record<number, number>
      linkOrder: Record<number, number>
    }

// Fallback per-item dwell (seconds) when an asset carries no duration of its
// own — matches the player's default rotation dwell.
const DEFAULT_DWELL = 10

// ── Publish burst guard ─────────────────────────────────────────────────────
//
// Every publish rewrites a screen's content channel and pushes a
// `content_update` to the device, which re-fetches its manifest and swaps what
// is on the wall. Firing several within seconds — pick, publish, pick again,
// publish again — makes the physical screen flicker through half-applied
// states while the operator is still deciding, and buries the real change in
// a stack of activity-log entries.
//
// So: a burst is allowed (revising a publish you just got wrong is normal and
// should stay frictionless), and only a SUSTAINED run is held back. Past the
// limit the operator is asked to wait out a short cooldown.
//
// This is a UX guard against content thrash, not a security control — it lives
// at the one routing layer every publish flow funnels through so no surface
// can bypass it by accident. Tune the three numbers here; nothing else reads
// them.
const PUBLISH_BURST_LIMIT     = 5       // publishes allowed inside the window
const PUBLISH_BURST_WINDOW_MS = 30_000  // rolling window they're counted over
const PUBLISH_COOLDOWN_MS     = 15_000  // wait imposed once the limit is hit

/** Timestamps of recent publishes, trimmed to the rolling window. */
let recentPublishes: number[] = []
/** When the current cooldown ends (0 = not cooling down). */
let cooldownUntil = 0

/** Thrown by `publishQuickTarget` when the burst limit has been hit. Callers
 *  show `retryAfterSec` rather than the generic "something went wrong" — the
 *  operator needs to know this is a deliberate pause, not a failure. */
export class PublishThrottleError extends Error {
  readonly retryAfterSec: number
  constructor(retryAfterSec: number) {
    super(`Too many publishes in a row — wait ${retryAfterSec}s.`)
    this.name = 'PublishThrottleError'
    this.retryAfterSec = retryAfterSec
  }
}

/** Record a publish and throw if the caller is over the burst limit. Called
 *  at the top of every publish route below. */
function guardPublishRate(): void {
  const now = Date.now()
  if (cooldownUntil > now) {
    throw new PublishThrottleError(Math.ceil((cooldownUntil - now) / 1000))
  }
  recentPublishes = recentPublishes.filter(t => now - t < PUBLISH_BURST_WINDOW_MS)
  if (recentPublishes.length >= PUBLISH_BURST_LIMIT) {
    cooldownUntil = now + PUBLISH_COOLDOWN_MS
    recentPublishes = []
    throw new PublishThrottleError(Math.ceil(PUBLISH_COOLDOWN_MS / 1000))
  }
  recentPublishes.push(now)
}

/** Test seam — resets the guard's module state between cases. */
export function __resetPublishRateGuard(): void {
  recentPublishes = []
  cooldownUntil = 0
}

/** The toast message for a failed publish. Throttling is a deliberate pause,
 *  not a failure, so it gets its own wording with the wait attached; anything
 *  else falls through to the API's own detail or the generic error. */
export function publishErrorMessage(
  err: any,
  t: (key: string, fallback: string, opts?: Record<string, unknown>) => string,
): string {
  if (err instanceof PublishThrottleError) {
    return t(
      'screens.publishThrottled',
      'Too many publishes in a row. Give the screens a moment — try again in {{seconds}}s.',
      { seconds: err.retryAfterSec },
    )
  }
  return err?.response?.data?.detail || t('common.error', 'Something went wrong')
}

const pickedName = (p: PickedItem): string =>
  p.kind === 'link' || p.kind === 'playlist' ? p.name : (p as { original_name?: string }).original_name ?? 'Content'

// Map a PlaylistContentPicker confirm to a publish target. Links publish
// through the menus endpoint (`/menus/{id}/publish`), hence link → menu. When
// several media/links are picked together, they're bundled into a brand-new
// playlist so ALL of them play (not just the first) — matching what an operator
// expects from a multi-select.
export function pickedItemToTarget(picked: PickedItem[]): QuickPublishTarget | null {
  if (picked.length === 0) return null

  const media = picked.filter(p => p.kind === 'media')
  const links = picked.filter(p => p.kind === 'link')
  const others = picked.filter(p => p.kind !== 'media' && p.kind !== 'link')

  // Multiple plain content pieces (no playlist/audio/stop-screen in the mix) →
  // auto-bundle into a new playlist and publish that.
  if (others.length === 0 && media.length + links.length > 1) {
    const extra = media.length + links.length - 1
    const base = pickedName(picked[0])
    // Positions come from each item's index in `picked` — the operator's real
    // click sequence, which the picker now preserves across kinds. Numbering
    // media 0..n and then links n+1..m (what this used to do) re-sorted every
    // mixed selection by type: pick link → image → link and the playlist came
    // out image, link, link. The rotation an operator builds is the rotation
    // that should play.
    const mediaDurations: Record<number, number> = {}
    const mediaOrder: Record<number, number> = {}
    const linkDurations: Record<number, number> = {}
    const linkOrder: Record<number, number> = {}
    picked.forEach((p, i) => {
      if (p.kind === 'media') {
        const dur = (p as { duration?: number }).duration
        mediaDurations[p.id] = Math.max(1, Math.round(dur && dur > 0 ? dur : DEFAULT_DWELL))
        mediaOrder[p.id] = i
      } else if (p.kind === 'link') {
        const dur = (p as { duration?: number }).duration
        linkDurations[p.id] = Math.max(1, Math.round(dur && dur > 0 ? dur : DEFAULT_DWELL))
        linkOrder[p.id] = i
      }
    })
    return {
      kind: 'new_playlist',
      name: extra > 0 ? `${base} +${extra}` : base,
      mediaIds: media.map(m => m.id),
      linkIds: links.map(l => l.id),
      mediaDurations, linkDurations, mediaOrder, linkOrder,
    }
  }

  const item = picked[0]
  if (item.kind === 'link') return { kind: 'menu', id: item.id }
  return { kind: item.kind, id: item.id }
}

// The PublishDialog `kind` only switches heading/toast strings — audio files
// and audio playlists read the same there; a new_playlist reads as a playlist.
export function publishDialogKind(
  target: QuickPublishTarget,
): 'media' | 'menu' | 'playlist' | 'audio' | 'stop_screen' | 'transition' {
  if (target.kind === 'audio_playlist') return 'audio'
  if (target.kind === 'new_playlist') return 'playlist'
  return target.kind
}

// Route the target to its publish endpoint with the shared
// screens/groups/window payload.
export async function publishQuickTarget(
  target: QuickPublishTarget,
  payload: PublishPayload,
): Promise<{ data: unknown }> {
  guardPublishRate()
  switch (target.kind) {
    // Several content pieces picked at once — create a playlist that bundles
    // them, then publish it like any other playlist.
    case 'new_playlist': {
      const { data } = await createPlaylist({
        name: target.name,
        media_ids: target.mediaIds,
        link_ids: target.linkIds,
        media_durations: target.mediaDurations,
        link_durations: target.linkDurations,
        media_order: target.mediaOrder,
        link_order: target.linkOrder,
      })
      return withCompanions(p => publishPlaylist(data.id, p))(payload)
    }
    // Playlists co-publish their linked companions (audio / stop screen /
    // transition) — the publish response carries the companion_* fields.
    case 'playlist':       return withCompanions(p => publishPlaylist(target.id, p))(payload)
    case 'media':          return publishMedia(target.id, payload)
    case 'menu':           return publishMenu(target.id, payload)
    case 'audio':          return publishAudio(target.id, payload)
    case 'audio_playlist': return publishAudioPlaylist(target.id, payload)
    case 'stop_screen':    return publishStopScreen(target.id, payload)
    // Transitions are a per-screen display setting — publish sets it on the
    // backend (which expands groups to their member screens).
    case 'transition':
      return publishTransition({
        key: target.key,
        screen_ids: payload.screen_ids,
        group_ids: payload.group_ids,
      })
  }
}
